import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Search, ArrowUp } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

/**
 * R153 — What's New viewer.
 * Renders parsed CHANGELOG.md rounds. Admins + PMs + boards can scan the
 * quarter's shipments to prep board reports.
 */
export default function WhatsNewPage() {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [selectedRound, setSelectedRound] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200, ...(q ? { q } : {}) };
      const { data } = await api.get("/admin/whats-new", { params });
      setEntries(data.items || []);
      setTotal(data.total || 0);
      if ((data.items || []).length && !selectedRound) {
        setSelectedRound(data.items[0].round);
      }
    } catch (err) { if (typeof console !== 'undefined') console.debug('pages/admin/WhatsNewPage.jsx: swallowed', err); } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  useEffect(() => { load(); }, [load]);

  const current = useMemo(
    () => entries.find((e) => e.round === selectedRound) || entries[0],
    [entries, selectedRound]
  );

  return (
    <div className="space-y-4" data-testid="whats-new-page">
      <PageHeader
        eyebrow="Portal"
        title="What's New"
        subtitle={`Track every release. ${total} rounds shipped so far — great fodder for board reports.`}
      />

      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search rounds by keyword…"
                 className="pl-9 bg-white/[0.04] border-white/10 h-9"
                 data-testid="whats-new-search" />
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* Timeline */}
        <Card className="col-span-4 border-white/10 p-0 max-h-[75vh] overflow-y-auto"
              data-testid="whats-new-timeline">
          {loading && <div className="p-6 text-sm text-white/50">Loading…</div>}
          {!loading && entries.length === 0 && (
            <div className="p-6 text-sm text-white/50">No matching rounds.</div>
          )}
          <ul className="divide-y divide-white/5">
            {entries.map((e) => (
              <li key={e.slug}>
                <button onClick={() => setSelectedRound(e.round)}
                        className={`w-full text-left p-3 hover:bg-white/[0.03] transition ${
                          selectedRound === e.round ? "bg-white/[0.05] border-l-2 border-orange-500" : ""
                        }`}
                        data-testid={`whats-new-round-${e.round}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-lg leading-none">{e.emoji}</span>
                    <span className="text-xs font-mono text-white/40">R{e.round}</span>
                    {e.date && (
                      <span className="text-[10px] text-white/40 ml-auto">
                        {new Date(e.date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                      </span>
                    )}
                  </div>
                  <div className="text-sm mt-1 line-clamp-2">{e.title}</div>
                </button>
              </li>
            ))}
          </ul>
        </Card>

        {/* Body */}
        <Card className="col-span-8 border-white/10 p-6 max-h-[75vh] overflow-y-auto"
              data-testid="whats-new-body">
          {current ? (
            <>
              <div className="flex items-start gap-3 mb-4">
                <span className="text-3xl leading-none">{current.emoji}</span>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="outline" className="border-orange-500/40 text-orange-300 bg-orange-500/10 font-mono text-[10px]">
                      Round {current.round}
                    </Badge>
                    {current.date && (
                      <span className="text-xs text-white/50">
                        Released {new Date(current.date).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
                      </span>
                    )}
                  </div>
                  <h2 className="text-xl font-semibold">{current.title}</h2>
                </div>
                <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                        className="text-white/40 hover:text-white/70"
                        data-testid="whats-new-scroll-top">
                  <ArrowUp className="h-4 w-4" />
                </button>
              </div>
              <article className="prose prose-invert prose-sm max-w-none prose-headings:text-white/90 prose-a:text-orange-400 prose-strong:text-white prose-code:text-orange-300 prose-code:bg-white/[0.05] prose-code:px-1 prose-code:rounded">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {current.body_md}
                </ReactMarkdown>
              </article>
            </>
          ) : (
            <div className="text-white/50 text-sm py-8 text-center">
              <Sparkles className="h-8 w-8 mx-auto mb-2 opacity-50" />
              Select a round on the left to see what shipped.
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
